import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  Input,
  Label,
  useToast,
} from "@nfp-tracker/components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GraduationCap, Link2, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { getInviteRoute } from "../constants/routes";
import {
  cancelInstructorInvite,
  inviteInstructor,
  listMyInstructors,
  listMyPendingInstructorInvites,
  revokeInstructorLink,
  type LinkedInstructor,
} from "../integrations/supabase/instructor";
import { handleExternalError } from "../utils/errorSnackbar";

type Props = {
  clientUserId: string;
  /** Name the invite email is signed with. */
  senderName: string;
};

// Keyed by the signed-in client so one account's instructor list can never be served to another.
const instructorLinksQueryKey = (clientUserId: string) => ["my-instructors", clientUserId] as const;
const instructorInvitesQueryKey = (clientUserId: string) =>
  ["my-instructor-invites", clientUserId] as const;

/**
 * Client-side control panel for instructor access: who can see this chart, who has been asked,
 * and a one-click way to cut access off. Instructor access is read-only, so there is no access
 * level selector here — unlike the spouse section.
 */
export const InstructorSharingSection = ({ clientUserId, senderName }: Props) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<LinkedInstructor | null>(null);

  const instructorsQuery = useQuery({
    queryKey: instructorLinksQueryKey(clientUserId),
    queryFn: listMyInstructors,
  });
  const invitesQuery = useQuery({
    queryKey: instructorInvitesQueryKey(clientUserId),
    queryFn: listMyPendingInstructorInvites,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: instructorLinksQueryKey(clientUserId) });
    void queryClient.invalidateQueries({ queryKey: instructorInvitesQueryKey(clientUserId) });
  };

  const handleInvite = async () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return;

    setSending(true);
    try {
      await inviteInstructor({
        clientUserId,
        instructorEmail: normalized,
        senderName,
      });

      toast({
        title: "Invitation sent",
        description: `${normalized} can now accept and view your chart.`,
      });
      setEmail("");
      refresh();
    } catch (error) {
      handleExternalError(toast, error, { logPrefix: "Error inviting instructor:" });
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = async (token: string) => {
    const url = `${window.location.origin}${getInviteRoute(token)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Invite link copied" });
    } catch {
      toast({ title: "Copy failed", description: url, variant: "destructive" });
    }
  };

  const handleCancel = async (inviteId: string) => {
    try {
      await cancelInstructorInvite(inviteId);
      toast({ title: "Invitation canceled" });
      refresh();
    } catch (error) {
      handleExternalError(toast, error, { logPrefix: "Error canceling instructor invite:" });
    }
  };

  const handleRevoke = async (link: LinkedInstructor) => {
    try {
      await revokeInstructorLink(link.linkId);
      toast({
        title: "Access removed",
        description: `${link.displayName || "Your instructor"} can no longer see your chart.`,
      });
      refresh();
    } catch (error) {
      handleExternalError(toast, error, { logPrefix: "Error revoking instructor access:" });
    }
  };

  const instructors = instructorsQuery.data ?? [];
  const invites = invitesQuery.data ?? [];

  return (
    <div className="space-y-4 pt-4 border-t">
      <div>
        <Label className="flex items-center gap-2 text-base font-semibold">
          <GraduationCap className="h-4 w-4" />
          Instructor Sharing
        </Label>
        <p className="mt-1 text-sm text-muted-foreground">
          Share your chart with an instructor. They get read-only access to your observations,
          calendar, and cycle history, and you can remove access at any time.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="instructorInviteEmail">Instructor email</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            id="instructorInviteEmail"
            type="email"
            placeholder="instructor@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void handleInvite();
            }}
          />
          <Button
            onClick={handleInvite}
            disabled={sending || !email.trim()}
            className="w-full sm:w-auto"
          >
            <Send className="mr-2 h-4 w-4" />
            Send
          </Button>
        </div>
      </div>

      {instructors.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Has access
          </Label>
          {instructors.map((instructor) => (
            <Card key={instructor.linkId} className="flex flex-wrap items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {instructor.displayName || instructor.publicEmail || "Instructor"}
                </p>
                {instructor.companyName && (
                  <p className="truncate text-xs text-muted-foreground">
                    {instructor.companyName}
                  </p>
                )}
                <Badge variant="outline" className="mt-1">
                  Read only
                </Badge>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => setPendingRevoke(instructor)}
              >
                <Trash2 className="mr-2 h-3 w-3" />
                Remove
              </Button>
            </Card>
          ))}
        </div>
      )}

      {invites.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Pending invitations
          </Label>
          {invites.map((invite) => (
            <Card key={invite.id} className="flex flex-wrap items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{invite.inviteeEmail}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Expires {new Date(invite.expiresAt).toLocaleDateString()}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleCopyLink(invite.token)}>
                <Link2 className="mr-2 h-3 w-3" />
                Copy link
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleCancel(invite.id)}>
                <Trash2 className="mr-2 h-3 w-3" />
                Cancel
              </Button>
            </Card>
          ))}
        </div>
      )}

      {instructors.length === 0 && invites.length === 0 && !instructorsQuery.isLoading && (
        <p className="text-sm text-muted-foreground">No instructor has access to your chart.</p>
      )}

      <AlertDialog
        open={Boolean(pendingRevoke)}
        onOpenChange={(open) => {
          if (!open) setPendingRevoke(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove instructor access?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRevoke?.displayName || "This instructor"} will immediately lose access to
              your observations and charts. You can invite them again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep access</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = pendingRevoke;
                setPendingRevoke(null);
                if (target) void handleRevoke(target);
              }}
            >
              Remove access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
